import { useId, type ReactNode } from 'react';
import { Check, Clock3 } from 'lucide-react';
import { Campo, clasesCon } from '@/components/Field';
import type { CalidadDato, EstadoCoccion, Nutrientes, TiempoComida } from '@/db/types';
import {
  ETIQUETAS_CALIDAD,
  ETIQUETA_ESTADO,
  NOMBRE_TIEMPO,
  TIEMPOS,
  formatearGramos,
  formatearKcal,
} from '@/domain/nutricion';
import { horaActual } from '@/domain/time';
import { esFechaLocal, type FechaLocal } from '@/domain/dates';

/**
 * Piezas compartidas por las pantallas de Comida (diseño Foco).
 */

// ---------------------------------------------------------------------------
// Etiquetas
// ---------------------------------------------------------------------------

/** Pastilla pequeña. Sobre el acento suave el texto va en `marca` (contraste 4.5:1). */
export function Etiqueta({ children, tono = 'marca' }: { children: ReactNode; tono?: 'marca' | 'aviso' }) {
  const clases =
    tono === 'aviso' ? 'bg-aviso-suave text-aviso' : 'bg-marca-suave text-marca';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-[3px] text-[11px] leading-none ${clases}`}
    >
      {children}
    </span>
  );
}

/** "Crudo" o "Cocido", o nada si no aplica. */
export function EtiquetaEstado({ estado }: { estado: EstadoCoccion | null }) {
  if (estado === null) return null;
  return <Etiqueta>{ETIQUETA_ESTADO[estado]}</Etiqueta>;
}

/**
 * De dónde salen los valores (0.3.5): "Etiqueta", "Referencia",
 * "Aproximación" o "Estimación". Las dos últimas en el tono de aviso, con su
 * texto: no se distinguen solo por el color.
 */
export function EtiquetaCalidad({ calidad }: { calidad: CalidadDato | null }) {
  if (calidad === null) return null;
  return (
    <Etiqueta tono={calidad === 'aproximada' || calidad === 'estimada' ? 'aviso' : 'marca'}>{ETIQUETAS_CALIDAD[calidad]}</Etiqueta>
  );
}

// ---------------------------------------------------------------------------
// Opciones en botones que se acomodan en varias líneas
// ---------------------------------------------------------------------------

export interface Opcion<T extends string> {
  valor: T;
  etiqueta: string;
}

interface PropsOpciones<T extends string> {
  etiqueta: string;
  valor: T;
  opciones: readonly Opcion<T>[];
  onCambio: (valor: T) => void;
  /** Columnas fijas; sin ellas, los botones se acomodan solos. */
  columnas?: 2 | 4;
}

/**
 * Como Segmentado, pero cabe en 320 px con opciones largas: los botones bajan
 * de línea en lugar de salirse. Cada opción mide al menos 44 puntos y la
 * elegida lleva palomita, no solo color (Req. 8.1, 8.5).
 */
export function Opciones<T extends string>({ etiqueta, valor, opciones, onCambio, columnas }: PropsOpciones<T>) {
  const id = useId();
  const rejilla =
    columnas === 4
      ? 'grid grid-cols-2 min-[400px]:grid-cols-4'
      : columnas === 2
        ? 'grid grid-cols-2'
        : 'flex flex-wrap';
  return (
    <div className="space-y-[7px]">
      <span id={id} className="block text-[13px] text-texto-suave">
        {etiqueta}
      </span>
      <div role="group" aria-labelledby={id} className={`${rejilla} gap-[3px] rounded-[15px] bg-superficie-suave p-1`}>
        {opciones.map((o) => {
          const elegida = o.valor === valor;
          return (
            <button
              key={o.valor}
              type="button"
              aria-pressed={elegida}
              onClick={() => onCambio(o.valor)}
              className={[
                'inline-flex min-h-11 items-center justify-center gap-1 rounded-xl px-3 text-center text-[14px] transition-colors',
                columnas === undefined ? 'grow' : '',
                elegida
                  ? 'bg-superficie font-medium text-texto shadow-[0_1px_3px_var(--sombra)]'
                  : 'text-texto-suave',
              ].join(' ')}
            >
              {elegida && <Check className="size-4 text-marca" />}
              {o.etiqueta}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const OPCIONES_TIEMPO: readonly Opcion<TiempoComida>[] = TIEMPOS.map((t) => ({
  valor: t,
  etiqueta: NOMBRE_TIEMPO[t],
}));

export function esTiempoComida(valor: string | null): valor is TiempoComida {
  return valor !== null && (TIEMPOS as readonly string[]).includes(valor);
}

export function fechaDeParametro(valor: string | null, respaldo: FechaLocal): FechaLocal {
  return valor !== null && esFechaLocal(valor) ? valor : respaldo;
}

/** La ruta del día de un registro, para volver ahí después de guardar. */
export function rutaDelDia(fecha: FechaLocal, hoy: FechaLocal, tiempo?: TiempoComida): string {
  const partes = [fecha === hoy ? null : `fecha=${fecha}`, tiempo === undefined ? null : `tiempo=${tiempo}`].filter(Boolean);
  return partes.length === 0 ? '/comida' : `/comida?${partes.join('&')}`;
}

// ---------------------------------------------------------------------------
// Hora con botón "Ahora" (Req. 18.7)
// ---------------------------------------------------------------------------

export function CampoHora({
  etiqueta,
  valor,
  onCambio,
  ayuda,
}: {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
}) {
  return (
    <Campo etiqueta={etiqueta} {...(ayuda !== undefined ? { ayuda } : {})}>
      {({ id, describedBy }) => (
        <div className="flex items-center gap-2">
          <input
            id={id}
            type="time"
            value={valor}
            onChange={(e) => onCambio(e.target.value)}
            aria-describedby={describedBy}
            className={`${clasesCon(false)} min-w-0 flex-1`}
          />
          <button
            type="button"
            onClick={() => onCambio(horaActual())}
            className="inline-flex min-h-12 shrink-0 items-center gap-1.5 rounded-[13px] bg-marca-suave px-3 text-[13px] font-medium text-texto"
          >
            <Clock3 className="size-4" />
            Ahora
          </button>
        </div>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------
// Valores
// ---------------------------------------------------------------------------

/** Vista previa de lo que se va a registrar (Req. 18.3). */
export function VistaValores({
  valores,
  gramos,
  nota,
}: {
  valores: Nutrientes;
  gramos: number | null;
  nota?: ReactNode;
}) {
  const macro = (nombre: string, g: number | null) => (
    <div className="min-w-0">
      <dt className="text-[12px] text-texto-suave">{nombre}</dt>
      <dd className="text-[15px] font-medium tabular-nums text-texto">
        {g === null ? 'Sin dato' : formatearGramos(g)}
      </dd>
    </div>
  );
  return (
    <div className="rounded-panel bg-superficie p-5" aria-live="polite">
      <span className="antetitulo">Vas a registrar</span>
      <p className="mt-2 text-[30px] leading-none tracking-[-1px] tabular-nums text-texto">
        {formatearKcal(valores.kcal)}
      </p>
      {gramos !== null && (
        <p className="mt-1.5 text-[12px] text-texto-suave">{formatearGramos(gramos)} en total</p>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3 min-[400px]:grid-cols-4">
        {macro('Proteína', valores.proteinaG)}
        {macro('Carbohidratos', valores.carbohidratosG)}
        {macro('Grasa', valores.grasaG)}
        {macro('Fibra', valores.fibraG)}
      </dl>
      {nota !== undefined && <div className="mt-3 text-[12px] leading-relaxed text-texto-suave">{nota}</div>}
    </div>
  );
}

/** Barra de avance. Decorativa: el texto junto a ella dice los números. */
export function Barra({ proporcion, pasada = false }: { proporcion: number | null; pasada?: boolean }) {
  if (proporcion === null) return null;
  return (
    <div aria-hidden="true" className="h-2 overflow-hidden rounded-full bg-superficie-suave">
      <div
        className={`h-full rounded-full ${pasada ? 'bg-aviso' : 'bg-marca'}`}
        style={{ width: `${Math.round(proporcion * 100)}%` }}
      />
    </div>
  );
}
